import React, { useEffect } from 'react';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import FloatingButtons from '../components/FloatingButtons/FloatingButtons';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: '✨',
      title: 'Excellence',
      description: 'We strive for excellence in every decoration we create, ensuring quality and attention to detail.'
    },
    {
      icon: '💝',
      title: 'Passion',
      description: 'Our passion for creating beautiful spaces drives us to go above and beyond for every client.'
    },
    {
      icon: '🤝',
      title: 'Trust',
      description: 'Building lasting relationships with our clients through transparency and reliability.'
    },
    {
      icon: '🎨',
      title: 'Creativity',
      description: 'We bring innovative and creative ideas to life, making each event unique and memorable.'
    }
  ];

  const stats = [
    { number: '5000+', label: 'Happy Customers' },
    { number: '10000+', label: 'Events Decorated' },
    { number: '50+', label: 'Cities Served' },
    { number: '5+', label: 'Years of Experience' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#8B1538] to-[#a01d47] text-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              About PartyNestt
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
              Creating magical moments and unforgettable celebrations since 2019
            </p>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#8B1538] mb-6">
                  Our Story
                </h2>
                <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                  <p>
                    PartyNestt was born from a simple belief: every celebration deserves to be extraordinary. 
                    What started as a small venture in 2019 has grown into a trusted name in event decoration 
                    in Ahmedabad and across Gujarat.
                  </p>
                  <p>
                    We specialize in creating stunning decorations for birthdays, anniversaries, proposals, 
                    baby showers, and all your special occasions. Our team of creative professionals works 
                    tirelessly to bring your vision to life.
                  </p>
                  <p>
                    From elegant balloon arches to intricate themed setups, we handle every detail with care 
                    and precision. Our commitment to quality and customer satisfaction has made us the preferred 
                    choice for thousands of celebrations.
                  </p>
                </div>
              </div>
              <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
                <img
                  src="/images/BannerImages/Bannerone.webp"
                  alt="Our Story"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#8B1538] mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 text-lg font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#8B1538] text-center mb-12">
              Our Values
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="text-5xl mb-4">{value.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-2xl font-bold text-[#8B1538] mb-4">Our Mission</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  To transform ordinary spaces into extraordinary experiences, making every celebration 
                  memorable and special. We aim to exceed expectations and create lasting impressions 
                  through our creative decorations and exceptional service.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="text-4xl mb-4">👁️</div>
                <h3 className="text-2xl font-bold text-[#8B1538] mb-4">Our Vision</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  To become India's most trusted and innovative event decoration company, known for 
                  creativity, quality, and customer satisfaction. We envision a future where every 
                  celebration is elevated through beautiful, personalized decorations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#8B1538] text-center mb-12">
              Why Choose PartyNestt?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: 'Expert Team',
                  description: 'Our skilled decorators bring years of experience and creativity to every project.'
                },
                {
                  title: 'Quality Materials',
                  description: 'We use only premium quality balloons, decorations, and materials for lasting impact.'
                },
                {
                  title: 'Custom Designs',
                  description: 'Every decoration is tailored to your preferences and theme, ensuring uniqueness.'
                },
                {
                  title: 'On-Time Delivery',
                  description: 'We respect your time and ensure all decorations are set up before your event begins.'
                },
                {
                  title: 'Affordable Pricing',
                  description: 'Quality decorations at competitive prices, making celebrations accessible to everyone.'
                },
                {
                  title: '24/7 Support',
                  description: 'Our customer support team is always ready to assist you with any queries or concerns.'
                }
              ].map((feature, index) => (
                <div key={index} className="p-6 border border-gray-200 rounded-lg hover:border-[#8B1538] hover:shadow-md transition-all">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-r from-[#8B1538] to-[#a01d47] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Make Your Event Special?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Let's work together to create an unforgettable celebration
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-white text-[#8B1538] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Contact Us
              </a>
              <a
                href="/products"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                View Our Work
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default About;

